import { Button, Icon } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import moment, { Moment } from "moment";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaBan, FaCheckCircle, FaEdit, FaExclamationCircle, FaRegClock } from "react-icons/fa";
import {
  BseBidStatusDto,
  SourcingEventSupplierView,
  useDeclineInvitationToBidMutation,
  useGetBseBidQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { isCreator } from "../../../../common/auth/utils";
import { useApiError } from "../../../../common/errors/useApiError";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { EditBidDraftButton } from "../../../bids/view-all/EditBidDraftButton";
import { CreateDraftButton } from "./buttons/CreateDraftButton";
import { EditDeliveredBidButton } from "./buttons/EditDeliveredBidButton";
import { GoToLatestRoundButton } from "./buttons/GoToLatestRoundButton";
import { ViewBidButton } from "./buttons/ViewBidButton";
import { useNegotiation } from "./NegotiationContext";
import { StatusBanner } from "./StatusBanner";

interface Props {
  event: SourcingEventSupplierView;
}

export const BseBidBanner = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const { roundNumber, currentRound } = useNegotiation();
  const authState = useLoggedInWithOrgContextState();

  const bidId = currentRound ? currentRound.bidId : event.createdBidId;

  const { data: createdBid, error, isLoading } = useGetBseBidQuery(bidId ? { bidId } : skipToken);

  const isLastRound = useMemo(() => {
    if (!event.negotiationRounds?.length) return true; // No rounds means original event is the only one
    return roundNumber === event.negotiationRounds.length; // Last round or original event if roundNumber is 0
  }, [event.negotiationRounds, roundNumber]);

  const currentDeadline = currentRound ? currentRound.deadline : event.deadline;

  const hasDeadlinePassed = deadlineHasPassed(moment(currentDeadline));

  const showActions = isLastRound && !hasDeadlinePassed;

  useEffect(() => {
    if (error) displayer.trigger(error);
  }, [displayer, error]);

  if (isLoading)
    return (
      <StatusBanner
        icon={FaRegClock}
        text={`${t("Loading bid status")}...`}
        border="1px solid"
        borderColor="smPrimarySofter"
        color="smPrimary"
      >
        <Button size="sm" isDisabled leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}>
          {t("Edit")}
        </Button>
        <Button size="sm" isDisabled leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}>
          {t("Decline")}
        </Button>
      </StatusBanner>
    );

  const activeStatus = currentRound?.status || event.status;

  switch (activeStatus) {
    case BseBidStatusDto.DeadlinePassedAndBidDelivered:
      if (!createdBid) throw Error("In deadline passed and bid delivered state, but no bid found.");
      return (
        <StatusBanner
          icon={FaCheckCircle}
          text={`${t("Your bid is delivered")}`}
          secondaryText={`${t("The deadline has now passed")}.`}
          border="1px solid"
          borderColor="smPrimarySofter"
          color="smPrimary"
        >
          <ViewBidButton bid={createdBid} />
          {!isLastRound && <GoToLatestRoundButton size="sm" />}
        </StatusBanner>
      );
    case BseBidStatusDto.BidDelivered:
      if (!createdBid) throw Error("In bid delivered state, but no bid found.");
      return (
        <StatusBanner
          icon={FaCheckCircle}
          text={`${t("Your bid is delivered")}`}
          secondaryText={
            !isLastRound
              ? `${t("There are later negotiation rounds available")}.`
              : hasDeadlinePassed
                ? `${t("The deadline has passed")}.`
                : ""
          }
          border="1px solid"
          borderColor="smPrimarySofter"
          color="smPrimary"
        >
          {showActions && (
            <EditDeliveredBidButton bid={createdBid} isDisabled={!isCreator(authState.selectedOrg.roles)} />
          )}
          <ViewBidButton bid={createdBid} />
          {!isLastRound && <GoToLatestRoundButton size="sm" />}
        </StatusBanner>
      );
    case BseBidStatusDto.DraftCreated:
      if (!createdBid) throw Error("In draft created state, but no draft found.");
      return (
        <StatusBanner
          icon={FaEdit}
          text={`${t("A draft has been created")}`}
          secondaryText={
            !isLastRound
              ? `${t("There are later negotiation rounds available")}.`
              : hasDeadlinePassed
                ? `${t("The deadline has passed")}.`
                : ""
          }
          border="1px solid"
          borderColor="smPrimarySofter"
          color="smPrimary"
        >
          {showActions && (
            <EditBidDraftButton bid={createdBid} isDisabled={!isCreator(authState.selectedOrg.roles)} size="sm" />
          )}
          {showActions && <DeclineButton eventId={event.id} isDisabled={!isCreator(authState.selectedOrg.roles)} />}
          {!isLastRound && <GoToLatestRoundButton size="sm" />}
        </StatusBanner>
      );
    case BseBidStatusDto.InviteAccepted:
    case BseBidStatusDto.InviteNotAccepted:
      return (
        <StatusBanner
          icon={FaExclamationCircle}
          text={`${t("Do you intend to deliver a bid?")}`}
          secondaryText={
            !isLastRound
              ? `${t("There are later negotiation rounds available")}.`
              : hasDeadlinePassed
                ? `${t("The deadline has passed")}.`
                : ""
          }
          border="1px solid"
          borderColor="smPrimarySofter"
          color="smPrimary"
        >
          {showActions && (
            <CreateDraftButton
              event={event}
              shouldAcceptInvite={false}
              isDisabled={!isCreator(authState.selectedOrg.roles)}
            />
          )}
          {showActions && <DeclineButton eventId={event.id} isDisabled={!isCreator(authState.selectedOrg.roles)} />}
          {!isLastRound && <GoToLatestRoundButton size="sm" />}
        </StatusBanner>
      );
    case BseBidStatusDto.DeadlinePassedWithoutBidDelivered:
      return (
        <StatusBanner
          icon={FaBan}
          text={`${t("Deadline has passed")}`}
          secondaryText={`${t("No bid was delivered")}`}
          border="1px solid"
          borderColor="smBackgroundTertiary"
          color="smMuted"
        />
      );
    case BseBidStatusDto.IsLoser:
      if (!createdBid) throw Error("In IsLoser state, but no bid found.");
      return (
        <StatusBanner
          icon={FaBan}
          text={`${t("You lost this event")}`}
          border="1px solid"
          borderColor="smPrimarySofter"
          color="smPrimary"
        >
          <ViewBidButton bid={createdBid} />
        </StatusBanner>
      );
    case BseBidStatusDto.IsWinner:
      if (!createdBid) throw Error("In IsWinner state, but no bid found.");
      return (
        <StatusBanner
          icon={FaCheckCircle}
          text={`${t("You won this event!")}`}
          border="1px solid"
          borderColor="smPrimarySofter"
          color="smPrimary"
        >
          <ViewBidButton bid={createdBid} />
        </StatusBanner>
      );
    case BseBidStatusDto.InviteDeclined:
      return (
        <StatusBanner
          icon={FaBan}
          text={`${t("Invitation declined")}`}
          border="1px solid"
          borderColor="smTertiarySoft"
          color="smTertiary"
        ></StatusBanner>
      );
    case BseBidStatusDto.EventCancelledAndBidDelivered:
      if (!createdBid) throw Error("In EventCancelledAndBidDelivered state, but no bid found.");
      return (
        <StatusBanner
          icon={FaBan}
          text={`${t("Event has been cancelled")}`}
          secondaryText={`${t("Your bid was delivered before cancellation")}.`}
          border="1px solid"
          borderColor="smBackgroundTertiary"
          color="smMuted"
        >
          <ViewBidButton bid={createdBid} />
        </StatusBanner>
      );
    case BseBidStatusDto.EventCancelledWithoutBidDelivered:
      return (
        <StatusBanner
          icon={FaBan}
          text={`${t("Event has been cancelled")}`}
          secondaryText={`${t("No bid was delivered")}.`}
          border="1px solid"
          borderColor="smBackgroundTertiary"
          color="smMuted"
        />
      );
    case BseBidStatusDto.EventClosedAndBidDelivered:
      if (!createdBid) throw Error("In EventClosedAndBidDelivered state, but no bid found.");
      return (
        <StatusBanner
          icon={FaCheckCircle}
          text={`${t("Event has been completed")}`}
          secondaryText={`${t("Your bid was delivered before completion")}.`}
          border="1px solid"
          borderColor="smBackgroundTertiary"
          color="smMuted"
        >
          <ViewBidButton bid={createdBid} />
        </StatusBanner>
      );
    case BseBidStatusDto.EventClosedWithoutBidDelivered:
      return (
        <StatusBanner
          icon={FaBan}
          text={`${t("Event has been completed")}`}
          secondaryText={`${t("No bid was delivered")}.`}
          border="1px solid"
          borderColor="smBackgroundTertiary"
          color="smMuted"
        />
      );
  }
};

const deadlineHasPassed = (deadline: Moment): boolean => {
  return moment().isAfter(deadline);
};

export const DeclineButton = ({ eventId, isDisabled = false }: { eventId: string; isDisabled?: boolean }) => {
  const [declineInvitation, { isLoading: isDeclining }] = useDeclineInvitationToBidMutation();

  return (
    <Explanation text={t("Decline the invitation to indicate that you will not deliver a bid")}>
      <Button
        colorScheme="red"
        variant="outline"
        leftIcon={<Icon as={FaBan} />}
        onClick={() => declineInvitation({ eventId })}
        isLoading={isDeclining}
        isDisabled={isDisabled}
        size="sm"
        ml="2"
      >
        {t("Decline")}
      </Button>
    </Explanation>
  );
};
